import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Clock, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { publicApi } from '../../lib/api';
import { formatPrice, categoryColors, cn } from '../../lib/utils';

export function RelatedPackages({ currentTourId, category, destination }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);

  const loadRelatedPackages = useCallback(async () => {
    try {
      // Try to get packages from same category first
      let response = await publicApi.getTours({ category, limit: 5 });
      let data = response.data?.data || response.data || [];
      
      // Filter out current tour
      data = data.filter(pkg => pkg.id !== currentTourId);
      
      // If not enough, get more
      if (data.length < 4) {
        const moreResponse = await publicApi.getFeaturedTours();
        const moreData = moreResponse.data?.data || moreResponse.data || [];
        const additional = moreData.filter(pkg => 
          pkg.id !== currentTourId && !data.find(d => d.id === pkg.id)
        );
        data = [...data, ...additional].slice(0, 4);
      }
      
      setPackages(data.slice(0, 4));
    } catch (error) {
      console.error('Error loading related packages:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTourId, category]);

  useEffect(() => {
    loadRelatedPackages();
  }, [loadRelatedPackages]);

  const scroll = (direction) => {
    const container = document.getElementById('related-carousel');
    if (container) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setScrollPosition(container.scrollLeft + scrollAmount);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Similar Packages You May Like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (packages.length === 0) return null;

  return (
    <section className="py-16 bg-[#FDFBF7]" data-testid="related-packages">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-2 block">
              You May Also Like
            </span>
            <h2 
              className="text-2xl md:text-3xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Similar Packages
            </h2>
          </div>
          <div className="hidden md:flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => scroll('left')}
              className="rounded-full border-gray-300 hover:border-[#FF6B35] hover:text-[#FF6B35]"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => scroll('right')}
              className="rounded-full border-gray-300 hover:border-[#FF6B35] hover:text-[#FF6B35]"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Carousel */}
        <div
          id="related-carousel"
          className="flex gap-6 overflow-x-auto scroll-smooth pb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {packages.map((pkg) => (
            <Link
              key={pkg.id}
              to={`/tours/${pkg.slug}`}
              className="flex-shrink-0 w-[280px] md:w-[calc(25%-18px)] group"
              style={{ scrollSnapAlign: 'start' }}
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={pkg.images?.[0] || 'https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg'}
                    alt={pkg.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <Badge className={cn(categoryColors[pkg.category] || 'bg-gray-100', 'shadow-sm')}>
                      {pkg.category}
                    </Badge>
                  </div>

                  {/* Price */}
                  <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                    <span className="font-bold text-[#004E64]">
                      {formatPrice(pkg.discounted_price || pkg.price)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-1 text-[#FF6B35] text-sm mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{pkg.destination}</span>
                  </div>
                  <h3 
                    className="font-semibold text-[#1A1A1A] mb-3 line-clamp-2 group-hover:text-[#FF6B35] transition-colors"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {pkg.name}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-[#4A4A4A]">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{pkg.duration_days}D/{pkg.duration_nights}N</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
                      <span>4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-8">
          <Link to={`/tours?category=${category}`}>
            <Button variant="outline" className="rounded-full px-8 border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white">
              View All {category} Tours
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
